import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";

/**
 * Bare S3 key under fileuploads/ (no prefix), e.g. "1779373886278_27819 PI.pdf"
 * @param {string} ref fileKey, path, or legacy presigned S3 URL
 * @returns {string|null}
 */
export function extractFileuploadBareKey(ref) {
  if (!ref || typeof ref !== "string") return null;
  const trimmed = ref.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("fileuploads/")) {
    return decodeURIComponent(
      trimmed.slice("fileuploads/".length).split("?")[0]
    );
  }

  if (trimmed.includes("/fileuploads/")) {
    const segment = trimmed.split("/fileuploads/").pop();
    if (segment) {
      return decodeURIComponent(segment.split("?")[0]);
    }
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const { pathname } = new URL(trimmed);
      const match = pathname.match(/\/fileuploads\/(.+)$/);
      if (match && match[1]) {
        return decodeURIComponent(match[1]);
      }
    } catch {
      return null;
    }
    return null;
  }

  return decodeURIComponent(trimmed.split("?")[0]);
}

export function isFileuploadStorageRef(ref) {
  return extractFileuploadBareKey(ref) != null;
}

/**
 * Nombre legible para mostrar al usuario (sin prefijo fileuploads/ ni timestamp).
 * @param {string} ref
 * @returns {string}
 */
export function getFileuploadDisplayName(ref) {
  if (!ref || typeof ref !== "string") return "";
  const bare = extractFileuploadBareKey(ref);
  let segment = bare;
  if (!segment) {
    try {
      if (ref.startsWith("http://") || ref.startsWith("https://")) {
        const { pathname } = new URL(ref);
        segment = decodeURIComponent(pathname.split("/").pop() || "");
      } else {
        segment = ref.split("?")[0].split("/").pop() || ref;
      }
    } catch {
      segment = ref;
    }
  }
  if (!segment) return "";
  const parts = segment.split("_");
  if (parts.length > 1 && /^\d+$/.test(parts[0])) {
    return parts.slice(1).join("_");
  }
  return segment;
}

/**
 * @param {object} error axios error
 * @param {(key: string) => string} [t]
 * @returns {string}
 */
export function resolveFileuploadAccessErrorMessage(error, t = (k) => k) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const serverMessage =
    (data && typeof data === "object" && data.message) ||
    (typeof data === "string" ? data : null);

  if (status === 404) {
    return serverMessage || t("fileNotFoundInStorage");
  }
  if (serverMessage) return serverMessage;
  return t("Error accessing file.");
}

/**
 * @param {string} ref
 * @returns {Promise<string|null>}
 */
export async function fetchFileuploadPresignedUrl(ref) {
  const bareKey = extractFileuploadBareKey(ref);
  if (!bareKey) return null;
  const response = await wencesApi.get(
    `/api/upload/file/${encodeURIComponent(bareKey)}`
  );
  return response.data?.url || null;
}

/**
 * @param {object} vm Vue component instance ($t)
 * @param {string} errorMessage
 * @param {{ title?: string, fileRef?: string, fileName?: string }} [options]
 */
export function showFileuploadAccessError(vm, errorMessage, options = {}) {
  const t = (key) => (vm && vm.$t ? vm.$t(key) : key);
  const fileName =
    options.fileName ||
    (options.fileRef ? getFileuploadDisplayName(options.fileRef) : "");
  const message = errorMessage || t("Error accessing file.");
  const text = fileName
    ? `${message}\n\n${t("File")}: ${fileName}`
    : message;

  Swal.fire({
    icon: "error",
    title: options.title || t("error"),
    text,
    confirmButtonText: t("ok"),
  });
}

/**
 * @param {string} ref fileKey or legacy URL
 * @param {{ t?: (key: string) => string }} [options]
 * @returns {Promise<{ ok: boolean, errorMessage?: string, status?: number, fileName?: string }>}
 */
export async function openFileupload(ref, options = {}) {
  const t = options.t || ((k) => k);
  const fileName = getFileuploadDisplayName(ref);
  const fail = (errorMessage, status) => ({
    ok: false,
    errorMessage,
    status,
    fileName,
  });

  if (!ref) return fail(t("Error accessing file."));

  try {
    const bareKey = extractFileuploadBareKey(ref);
    if (bareKey) {
      const url = await fetchFileuploadPresignedUrl(ref);
      if (url) {
        window.open(url, "_blank");
        return { ok: true };
      }
      return fail(t("Error accessing file."));
    }

    if (ref.startsWith("http://") || ref.startsWith("https://")) {
      window.open(ref, "_blank");
      return { ok: true };
    }

    return fail(t("Error accessing file."));
  } catch (error) {
    console.error("Error opening fileupload:", error);
    return {
      ok: false,
      status: error?.response?.status,
      errorMessage: resolveFileuploadAccessErrorMessage(error, t),
      fileName,
    };
  }
}
